import type { ReactNode } from 'react';
import { SITE_URL, altLinks, localePath, type Lang } from '../../lib/i18n';
import { hubStrings, toolUrl, webAppLd, faqLd, breadcrumbLd } from '../../lib/tools';
import { BlogHeader } from '../BlogHeader';
import { Footer } from '../Footer';
import { Seo } from '../Seo';
import { Breadcrumbs, EmbedSection, ToolCta, ToolFaq, ToolHeroText } from './ToolParts';

interface Copy {
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  h1: string;
  intro: string;
  howItWorks: { heading: string; steps: { title: string; body: string }[] };
  faq: { heading: string; items: { q: string; a: string }[] };
  cta: { heading: string; body: string };
  embed: { frameTitle: string; credit: string };
}

/**
 * Shared landing-page shell for every /tools/<slug> page: SEO head (title, meta,
 * canonical, hreflang, OG via <Seo>), WebApplication + FAQPage + BreadcrumbList
 * JSON-LD, H1 + intro, the tool above the fold, then how-it-works, a worked
 * example, app CTA, embed snippet and FAQ.
 */
export function ToolLanding({
  lang,
  slug,
  copy,
  tool,
  example,
  extra,
  embedHeight,
  overrides,
}: {
  lang: Lang;
  slug: string;
  copy: Copy;
  tool: ReactNode;
  example?: ReactNode;
  extra?: ReactNode;
  embedHeight: number;
  /** For pre-rendered variants (e.g. a matchup pair page). */
  overrides?: { title: string; description: string; h1: string; intro: string; path: string; alternatesPath: string };
}) {
  const h = hubStrings(lang);
  const pageUrl = overrides ? `${SITE_URL}${localePath(lang, overrides.path)}` : toolUrl(lang, slug);
  const toolPageUrl = toolUrl(lang, slug);
  const title = overrides?.title ?? copy.metaTitle;
  const description = overrides?.description ?? copy.metaDescription;
  const crumbs = [
    { name: h.home, url: `${SITE_URL}${localePath(lang, '/')}` },
    { name: h.navLabel, url: `${SITE_URL}${localePath(lang, '/tools')}` },
    { name: copy.h1, url: toolPageUrl },
  ];
  if (overrides) crumbs.push({ name: overrides.h1, url: pageUrl });

  return (
    <div className="min-h-screen bg-dark">
      <Seo
        title={title}
        description={description}
        canonical={pageUrl}
        lang={lang}
        alternates={altLinks(overrides?.alternatesPath ?? `/tools/${slug}`)}
        jsonLd={[
          webAppLd({ name: copy.h1, description: copy.metaDescription, url: toolPageUrl, lang }),
          faqLd(copy.faq.items),
          breadcrumbLd(crumbs),
        ]}
      />
      <div className="print:hidden">
        <BlogHeader lang={lang} />
      </div>

      <main className="container mx-auto px-4 sm:px-6 py-10 md:py-14 max-w-6xl">
        <Breadcrumbs lang={lang} current={copy.h1} />
        <div className="print:hidden">
          <ToolHeroText eyebrow={copy.eyebrow} h1={overrides?.h1 ?? copy.h1} intro={overrides?.intro ?? copy.intro} />
        </div>

        <section className="mb-14" aria-label={copy.h1}>
          {tool}
        </section>

        <div className="print:hidden max-w-4xl">
          {extra}
          <section className="mb-14" aria-labelledby="how-heading">
            <h2 id="how-heading" className="text-2xl md:text-3xl font-extrabold text-ink mb-5">{copy.howItWorks.heading}</h2>
            <ol className="grid sm:grid-cols-2 gap-3">
              {copy.howItWorks.steps.map((s, i) => (
                <li key={i} className="card-float p-5">
                  <span className="chip-icon w-9 h-9 font-black mb-3">{i + 1}</span>
                  <h3 className="font-black text-ink mb-1">{s.title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{s.body}</p>
                </li>
              ))}
            </ol>
          </section>

          {example}

          <ToolCta lang={lang} heading={copy.cta.heading} body={copy.cta.body} />

          <EmbedSection lang={lang} slug={slug} title={copy.embed.frameTitle} credit={copy.embed.credit} height={embedHeight} />

          <ToolFaq heading={copy.faq.heading} items={copy.faq.items} />
        </div>
      </main>

      <div className="print:hidden">
        <Footer lang={lang} />
      </div>
    </div>
  );
}
